import { useResizeObserver } from '@vueuse/core'

type TActiveIndex = ComputedRef<number | null> | Ref<number | null>

export function useSlidingIndicatorBar(activeIndex: TActiveIndex) {
  const containerRef = ref<HTMLElement | null>(null)
  const itemRefs = ref<HTMLElement[]>([])
  const hoveredIndex = ref<number | null>(null)
  const currentIndicatorIndex = computed(() => hoveredIndex.value ?? activeIndex.value)
  const indicatorBar = reactive({ left: 0, width: 0, visible: false })

  function getItemMetrics(index: number | null) {
    if (!containerRef.value || index === null) {
      return null
    }

    const itemElement = itemRefs.value[index]
    if (!itemElement) {
      return null
    }

    const containerRect = containerRef.value.getBoundingClientRect()
    const itemRect = itemElement.getBoundingClientRect()

    return {
      left: itemRect.left - containerRect.left,
      width: itemRect.width
    }
  }

  function updateIndicatorBarPosition(index: number | null) {
    const metrics = getItemMetrics(index)

    if (!metrics) {
      indicatorBar.visible = false

      return
    }

    indicatorBar.left = metrics.left
    indicatorBar.width = metrics.width
    indicatorBar.visible = true
  }

  function handleIndicatorEnter(index: number) {
    hoveredIndex.value = index

    if (index === activeIndex.value) {
      return
    }

    updateIndicatorBarPosition(index)
  }

  function handleIndicatorLeave() {
    hoveredIndex.value = null
    updateIndicatorBarPosition(activeIndex.value)
  }

  onBeforeUpdate(() => {
    itemRefs.value = []
  })

  onUpdated(() => {
    updateIndicatorBarPosition(currentIndicatorIndex.value)
  })

  watch(
    () => currentIndicatorIndex.value,
    async () => {
      await nextTick()
      updateIndicatorBarPosition(currentIndicatorIndex.value)
    }
  )

  useResizeObserver(containerRef, () => updateIndicatorBarPosition(currentIndicatorIndex.value))

  return {
    containerRef,
    itemRefs,
    indicatorBar,
    handleIndicatorEnter,
    handleIndicatorLeave,
    updateIndicatorBarPosition
  }
}
